// A tiny cached view of "who is signed in", refreshed from `/api/auth/me`. Owns
// the site-wide presence socket lifecycle (§4.1): connected while signed in,
// closed on sign-out.

import { api, type User } from "./api.ts";
import { startPresence, stopPresence } from "./presence.ts";

let current: User | null = null;
let browserPlay = false;

/** New play owns its foreground lease; never compete with the old session socket. */
export function usePlaySession(): void {
  browserPlay = true;
  stopPresence();
}

export function currentUser(): User | null {
  return current;
}

/** Reflect a presence socket for the current sign-in state. */
function syncPresence(): void {
  if (current && !browserPlay) startPresence();
  else stopPresence();
}

export function setUser(user: User | null): void {
  if (current?.id !== user?.id) stopPresence();
  current = user;
  syncPresence();
}

/** Re-fetch the session; sets `null` on any failure (including 401). */
export async function refreshUser(): Promise<User | null> {
  try {
    setUser(await api.me());
  } catch {
    setUser(null);
  }
  return current;
}

// Whether the public "Become a member" form is open (superuser-controlled). Cached
// per page load so the login screen doesn't refetch on every mount. Fail-closed:
// if /api/config can't load, hide the CTA rather than advertise a form we can't
// confirm is open.
let membershipOpenCache: Promise<boolean> | null = null;
export function membershipOpen(): Promise<boolean> {
  if (!membershipOpenCache) {
    membershipOpenCache = api.getConfig().then((c) => c.membership_open).catch(() => false);
  }
  return membershipOpenCache;
}

let codeLoginEnabledCache: Promise<boolean> | null = null;
export function codeLoginEnabled(): Promise<boolean> {
  if (!codeLoginEnabledCache) {
    codeLoginEnabledCache = api
      .getConfig()
      .then((config) => config.code_login_enabled)
      .catch(() => false);
  }
  return codeLoginEnabledCache;
}
